import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { tagService, Tag } from '../../services/tagService';
import { fetchRelatedPosts } from '../../services/api';
import { WordPressPost } from '../../types/wordpress';
import { MobileGridSkeleton } from '../SkeletonLoader';
import { useSwipe } from '../SwipeProvider';
import { useResponsiveImage } from '../../hooks/useOptimizedImage';
import { useLanguageDetection } from '../../hooks/useLanguageDetection';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { formatDate } from '../../utils/dateUtils';

// Device detection hook
const useDeviceType = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
  });
  
  useEffect(() => {
    const checkDevice = () => {
      if (typeof window === 'undefined') return;
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileWidth = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isMobileWidth);
    };
    
    // Debounced resize handler
    let timeoutId: number;
    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(checkDevice, 150);
    };

    window.addEventListener('resize', debouncedCheck);
    checkDevice(); // Check immediately
    
    return () => {
      window.removeEventListener('resize', debouncedCheck);
      clearTimeout(timeoutId);
    };
  }, []);
  
  return isMobile;
};

// ======================== MOBILE STYLED COMPONENTS ========================
const RelatedContainer = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  transform: ${({ isVisible }) => isVisible ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  overflow: hidden;
  
  /* Enhanced layer feel */
  box-shadow: ${({ isVisible }) => isVisible ? '-10px 0 30px rgba(0, 0, 0, 0.5)' : 'none'};
  will-change: transform;
  backface-visibility: hidden;
  
  /* Smooth hardware acceleration */
  -webkit-transform: ${({ isVisible }) => isVisible ? 'translateX(0) translateZ(0)' : 'translateX(100%) translateZ(0)'};
`;

const RelatedHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #000;
  border-bottom: 1px solid #333;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const RelatedBackButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 18px;
  padding: 8px;
  cursor: pointer;
  margin-right: 12px;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:active {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const RelatedInfo = styled.div`
  flex: 1;
`;

const RelatedTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  color: #fff;
`;

const RelatedSubtitle = styled.div`
  font-size: 14px;
  color: #aaa;
  margin: 0;
`;

// ======================== MOBILE RESPONSIVE GRID (VIEWPORT-BASED 3x3) ========================
const RelatedPostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px; /* Minimal gap for clean look */
  padding: 0;
  margin: 0;
  width: 100%;
  background: #000;
  flex: 1;
  overflow-y: auto;
  
  /* Calculate grid item height based on viewport */
  /* Each row = (viewport height - header) / 3 */
  grid-auto-rows: calc((100vh - 60px) / 3);
  place-items: stretch;
  
  /* Smooth scrolling on mobile */
  -webkit-overflow-scrolling: touch;
  
  /* Ensure grid items fill exactly 1/3 of viewport */
  & > * {
    height: calc((100vh - 60px) / 3);
    min-height: calc((100vh - 60px) / 3);
    max-height: calc((100vh - 60px) / 3);
  }
`;

const RelatedPostGridItem = styled.div`
  /* Fixed viewport-based dimensions - exactly 1/3 of viewport */
  width: 100%;
  height: calc((100vh - 60px) / 3); /* Exact 1/3 of available viewport */
  position: relative;
  cursor: pointer;
  overflow: hidden;
  background: #1a1a1a;
  user-select: none;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0.1);
  
  /* Enhanced mobile interaction */
  touch-action: auto;
  pointer-events: auto;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  /* Visual feedback states */
  transition: background 0.15s ease, transform 0.1s ease;
  
  &:hover {
    cursor: pointer;
    background: #2a2a2a;
  }
  
  &:active {
    transform: scale(0.98);
    background: #2a2a2a;
  }
  
  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
  
  /* Prevent accidental activation during scroll */
  &.scrolling {
    pointer-events: none;
  }
`;

const RelatedPostThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none !important;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  
  /* Enhanced image quality */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  
  /* Smooth loading transition */
  opacity: 1;
  transition: opacity 0.3s ease;
  
  /* Prevent blurry upscaling */
  image-rendering: auto;
  -ms-interpolation-mode: bicubic;
  
  /* Loading state */
  &[src=""], &:not([src]) {
    opacity: 0.3;
    background: #2a2a2a;
  }
  
  /* Error state styling */
  &[src$="logo_infonium.png"] {
    object-fit: contain;
    background: #1a1a1a;
    padding: 20%;
  }
`;

// Optimized Related Post Thumbnail Component
interface RelatedPostThumbnailProps {
  src: string;
  alt: string;
  loading?: 'lazy' | 'eager';
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

const RelatedPostThumbnail: React.FC<RelatedPostThumbnailProps> = ({ 
  src, 
  alt, 
  loading = 'lazy',
  onError 
}) => {
  // Use optimized image for mobile related post grid
  const optimizedImage = useResponsiveImage(src, 'related-posts-grid', true);
  
  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn('Related post thumbnail failed, fallback to original:', optimizedImage.src);
    if (onError) {
      onError(e);
    } else {
      const target = e.target as HTMLImageElement;
      target.src = 'https://cdn.infonium.org/img/logo_infonium.png';
    }
  };

  return (
    <RelatedPostThumbnailImg
      src={optimizedImage.src}
      alt={alt}
      loading={loading}
      onError={handleError}
      title={`${alt} - Optimized: ${optimizedImage.isOptimized ? optimizedImage.size : 'Original'} (related-posts-grid)`}
    />
  );
};

const RelatedPostOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: clamp(4px, 1.5vw, 8px);
  pointer-events: none;
`;

const RelatedPostTitle = styled.div`
  color: #fff;
  font-size: clamp(8px, 2.5vw, 12px); /* Responsive font size */
  font-weight: 600;
  line-height: 1.2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RelatedPostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: clamp(7px, 2vw, 10px); /* Responsive font size */
  color: #ccc;
  margin-top: 2px;
`;

const RelatedAudioIndicator = styled.span`
  &:before {
    content: '🎵';
    font-size: clamp(6px, 1.8vw, 9px);
  }
`;

const RelatedScrollToTopButton = styled.button<{ visible: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  cursor: pointer;
  z-index: 1000;
  display: ${({ visible }) => visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Loading and Error Components
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #000;
  color: #fff;
  font-size: 18px;
  flex-direction: column;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #000;
  color: #fff;
  text-align: center;
  padding: 20px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  text-align: center;
  color: #aaa;
`;

// ======================== RELATED GRID COMPONENT ========================
const RelatedGrid: React.FC<{
  tags: Tag[];
  posts: WordPressPost[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onPostClick: (post: WordPressPost) => void;
  onClose: () => void;
  currentPost?: { id: number; title: string; tags: number[]; slug: string; } | null;
  shortTitle?: string;
}> = ({ tags, posts, loading, loadingMore, hasMore, onLoadMore, onPostClick, onClose, currentPost, shortTitle }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Touch gesture tracking for swipe vs tap
  const touchDataRef = useRef<{
    startX: number;
    startY: number;
    startTime: number;
    moved: boolean;
    threshold: number;
  }>({
    startX: 0,
    startY: 0,
    startTime: 0,
    moved: false,
    threshold: 10
  });
  
  // Restore scroll detection for UI
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const scrollTop = container.scrollTop;
      
      setShowScrollTop(scrollTop > 500);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPostThumbnail = (post: WordPressPost): string => {
    if (post.imageUrls && post.imageUrls.length > 0) {
      return post.imageUrls[0];
    }
    return 'https://cdn.infonium.org/img/logo_infonium.png';
  };

  const cleanTitle = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  };

  // Remove local formatDate function - using imported one from dateUtils

  // Enhanced touch handlers
  const handleTouchStart = (e: React.TouchEvent, post: WordPressPost) => {
    const touch = e.touches[0];
    touchDataRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      moved: false,
      threshold: 10
    };
    
    console.log('[RelatedGrid] 📱 Post TouchStart:', { 
      postId: post.id, 
      coordinates: { x: touch.clientX, y: touch.clientY }
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchDataRef.current.startTime) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchDataRef.current.startX);
    const deltaY = Math.abs(touch.clientY - touchDataRef.current.startY);
    
    if (deltaX > touchDataRef.current.threshold || deltaY > touchDataRef.current.threshold) {
      touchDataRef.current.moved = true;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent, post: WordPressPost) => {
    const touchDuration = Date.now() - touchDataRef.current.startTime;
    const wasMoved = touchDataRef.current.moved;
    
    console.log('[RelatedGrid] 📱 Post TouchEnd Analysis:', { 
      postId: post.id,
      duration: touchDuration,
      moved: wasMoved,
      decision: !wasMoved && touchDuration < 500 ? 'VALID_TAP' : 'IGNORE_SWIPE'
    });
    
    if (!wasMoved && touchDuration < 500) {
      console.log('[RelatedGrid] ✅ Valid tap detected, navigating to post:', post.id);
      onPostClick(post);
    } else {
      console.log('[RelatedGrid] 🚫 Swipe/drag detected, ignoring tap:', { 
        postId: post.id, 
        reason: wasMoved ? 'moved' : 'too_long' 
      });
    }
    
    // Reset touch data
    touchDataRef.current = {
      startX: 0,
      startY: 0,
      startTime: 0,
      moved: false,
      threshold: 10
    };
  };

  const renderPostItem = (post: WordPressPost, index: number) => (
    <RelatedPostGridItem 
      key={`${post.id}-${index}`}
      tabIndex={0}
      role="button"
      aria-label={`Tap to view ${cleanTitle(post.title.rendered)}`}
      onTouchStart={(e) => handleTouchStart(e, post)}
      onTouchMove={handleTouchMove}
      onTouchEnd={(e) => handleTouchEnd(e, post)}
      onClick={(e) => {
        const pointerType = (e.nativeEvent as PointerEvent)?.pointerType;
        const isTouchDevice = 'ontouchstart' in window;
        
        console.log('[RelatedGrid] 🖱️ Click event:', { 
          postId: post.id,
          pointerType,
          isTouchDevice,
          eventType: e.type
        });
        
        if (!isTouchDevice || pointerType === 'mouse') {
          e.preventDefault();
          e.stopPropagation();
          console.log('[RelatedGrid] ✅ Handling click navigation for post:', post.id);
          onPostClick(post);
        } else {
          console.log('[RelatedGrid] ⏭️ Skipping click - will be handled by touch events');
        }
      }}
      title={cleanTitle(post.title.rendered)}
    >
      <RelatedPostThumbnail
        src={getPostThumbnail(post)}
        alt={cleanTitle(post.title.rendered)}
        loading="lazy"
      />
      <RelatedPostOverlay>
        <RelatedPostTitle>{cleanTitle(post.title.rendered)}</RelatedPostTitle>
        <RelatedPostMeta>
          {post.audioUrl && <RelatedAudioIndicator />}
          <span>{formatDate(post.date)}</span>
        </RelatedPostMeta>
      </RelatedPostOverlay>
    </RelatedPostGridItem>
  );

  return (
    <>
      <RelatedHeader>
        <RelatedBackButton onClick={onClose} title="Đóng trang liên quan">
          ←
        </RelatedBackButton>
        <RelatedInfo>
          <RelatedTitle>
            {currentPost && posts.length > 0 
              ? `${posts.length} related articles`
              : 'Related Articles'
            }
          </RelatedTitle>
          <RelatedSubtitle>
            {loading ? 'Loading...' : 
              currentPost && posts.length > 0 
                ? `From "${shortTitle || 'this article'}..."`
                                  : posts.length > 0 
                  ? `${posts.length} articles • From top tags`
                : 'No related articles yet'
            }
          </RelatedSubtitle>
        </RelatedInfo>
      </RelatedHeader>

      {loading && posts.length === 0 ? (
        <MobileGridSkeleton count={9} showHeader={false} />
      ) : posts.length === 0 && !loading ? (
        <EmptyState>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔗</div>
          <div style={{ fontSize: '18px', marginBottom: '8px' }}>
            No related articles yet
          </div>
          <div style={{ fontSize: '14px' }}>
                          Please return to homepage to explore more content
          </div>
        </EmptyState>
      ) : (
        <>
          <RelatedPostGrid ref={containerRef}>
            {posts.map((post, index) => renderPostItem(post, index))}
          </RelatedPostGrid>
        </>
      )}

      <RelatedScrollToTopButton visible={showScrollTop} onClick={scrollToTop} title="Lên đầu trang">
        ↑
      </RelatedScrollToTopButton>
      
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: calc(200px + 100%) 0; }
        }
      `}</style>
    </>
  );
};

// ======================== MAIN RELATED PAGE COMPONENT ========================
const RelatedPage: React.FC<{ 
  isVisible: boolean; 
  onClose: () => void; 
}> = ({ isVisible, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useDeviceType();
  const { currentPost } = useSwipe();
  const { getCurrentLanguageCategoryId } = useLanguageDetection();
  
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Swipe-to-close state
  const [isSwipeTracking, setIsSwipeTracking] = useState(false);
  const [swipeStartX, setSwipeStartX] = useState(0);
  const [swipeStartY, setSwipeStartY] = useState(0);
  const [currentSwipeX, setCurrentSwipeX] = useState(0);
  const [swipeProgress, setSwipeProgress] = useState(0);

  // 🔍 Helper function to get clean title from various sources with HTML entity decoding
  const getCurrentPostTitle = useCallback(() => {
    // Helper function to decode HTML entities
    const decodeHtmlEntities = (text: string): string => {
      try {
        const div = document.createElement('div');
        div.innerHTML = text;
        return div.textContent || div.innerText || text;
      } catch (error) {
        console.warn('[RelatedPage] ⚠️ Could not decode HTML entities:', error);
        return text;
      }
    };

    // Helper function to clean and decode title
    const cleanAndDecodeTitle = (rawTitle: string): string => {
      // First remove HTML tags
      const withoutTags = rawTitle.replace(/<[^>]*>/g, '').trim();
      // Then decode HTML entities like &#8211; (em dash), &quot;, etc.
      const decoded = decodeHtmlEntities(withoutTags);
      return decoded;
    };

    // Method 1: From SwipeProvider currentPost (most reliable)
    if (currentPost?.title) {
      if (typeof currentPost.title === 'string') {
        return cleanAndDecodeTitle(currentPost.title);
      }
      if (typeof currentPost.title === 'object' && (currentPost.title as any)?.rendered) {
        return cleanAndDecodeTitle((currentPost.title as any).rendered);
      }
    }
    
    // Method 2: Extract from current URL as fallback
    try {
      const currentPath = window.location.pathname;
      console.log('[RelatedPage] 🔍 Extracting title from URL:', currentPath);
      
      // Parse URL pattern: /post/{id}/{slug}.html
      const urlMatch = currentPath.match(/\/post\/\d+\/(.+)\.html/);
      if (urlMatch && urlMatch[1]) {
        // Get raw slug and decode URL encoding first
        const rawSlug = urlMatch[1];
        
        // Decode URL encoding (handle %20, %C3%A9, etc.)
        let decodedSlug = rawSlug;
        try {
          decodedSlug = decodeURIComponent(rawSlug);
        } catch (decodeError) {
          console.warn('[RelatedPage] ⚠️ Could not decode URL, using raw slug:', rawSlug);
          decodedSlug = rawSlug;
        }
        
        // Enhanced slug to title conversion
        const readableTitle = decodedSlug
          .split('-')
          .map(word => {
            // Handle empty words (consecutive dashes)
            if (!word) return '';
            
            // Preserve Vietnamese characters and special characters
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          })
          .filter(word => word.length > 0) // Remove empty segments
          .join(' ');
        
        console.log('[RelatedPage] 🎯 Title from URL slug:', {
          rawSlug,
          decodedSlug,
          readableTitle
        });
        
        return readableTitle;
      }
    } catch (error) {
      console.error('[RelatedPage] ❌ Error extracting title from URL:', error);
    }
    
    // Method 3: Final fallback
    return 'Current article';
  }, [currentPost]);

  console.log('[RelatedPage] 📱 Component state:', { 
    isVisible,
    isMobile, 
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 'undefined',
    postsCount: posts.length,
    currentPostTitle: getCurrentPostTitle(),
    currentPostFromContext: currentPost ? {
      id: currentPost.id,
      title: currentPost.title,
      titleType: typeof currentPost.title
         } : null
  });

  // 🎯 Memoized title and descriptions for SEO
  const seoData = useMemo(() => {
    const cleanTitle = getCurrentPostTitle();
    const shortTitle = cleanTitle.substring(0, 80);
    const mediumTitle = cleanTitle.substring(0, 100);
    const truncatedTitle = cleanTitle.substring(0, 120);
    const veryShortTitle = cleanTitle.substring(0, 60);
    
    return {
      pageTitle: currentPost && posts.length > 0
        ? `Related Articles: ${shortTitle} | ${posts.length} articles - Infonium`
        : currentPost 
        ? currentPost && mediumTitle.length <= 50
          ? `Related Articles: ${mediumTitle} - Infonium`
          : 'Related Articles - Infonium'
        : 'Related Articles - Infonium',
      
      description: currentPost && posts.length > 0
        ? `Discover ${posts.length} related articles about "${truncatedTitle}". Similar high-quality content on Infonium.`
        : currentPost 
        ? currentPost && truncatedTitle.length <= 50
          ? `Discover related articles about "${truncatedTitle}". Quality content on Infonium.`
          : 'Discover interesting related articles on Infonium. Quality content, continuously updated.'
        : 'Discover interesting related articles on Infonium. Quality content, continuously updated.',
      
      keywords: currentPost && posts.length > 0
        ? `related articles, ${cleanTitle.toLowerCase().substring(0, 50)}, similar content, ${posts.length} articles, infonium, mobile grid`
        : `related articles, similar content, discover more, infonium${currentPost ? `, ${veryShortTitle}` : ''}`,
      
      ogTitle: currentPost && posts.length > 0
        ? `${posts.length} related articles: ${shortTitle}`
        : currentPost 
        ? currentPost && mediumTitle.length <= 30
          ? `Related Articles: ${mediumTitle}`
          : 'Related Articles - Infonium'
        : 'Related Articles - Infonium',
      
      shortTitle: veryShortTitle
    };
  }, [getCurrentPostTitle, currentPost, posts.length]);

  // Optimized data loading using new API
  const loadRelatedData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setPosts([]); // Clear previous data
      
      console.log('[RelatedPage] 🎯 Loading related posts with optimized API...');
      
      // Get current post info from context (better than URL parsing)
      let currentPostId: number | null = null;
      let currentPostTags: number[] = [];
      let currentPostForTitle: any = currentPost; // Store for title display
      
      if (currentPost) {
        currentPostId = currentPost.id;
        currentPostTags = currentPost.tags || [];
        console.log('[RelatedPage] 📍 Using current post from context:', {
          id: currentPostId,
          title: (currentPost.title as any)?.rendered?.substring(0, 50) + '...' || 'Untitled',
          tags: currentPostTags.length
        });
      } else {
        // Fallback: Try to extract from URL and fetch post info
        const urlPath = window.location.pathname;
        const postIdMatch = urlPath.match(/\/post\/(\d+)\//);
        if (postIdMatch) {
          currentPostId = parseInt(postIdMatch[1]);
          console.log('[RelatedPage] 📍 Found current post ID from URL (fallback):', currentPostId);
          
          // Fetch current post details for better context
          try {
            const { fetchPostById } = await import('../../services/api');
            const currentPostDetails = await fetchPostById(currentPostId);
            if (currentPostDetails) {
              currentPostTags = currentPostDetails.tags || [];
              currentPostForTitle = currentPostDetails; // Use for title display
              console.log('[RelatedPage] ✅ Fetched current post details from API:', {
                id: currentPostId,
                title: currentPostDetails.title?.rendered?.substring(0, 50) + '...' || 'Untitled',
                tags: currentPostTags.length
              });
            }
          } catch (fetchError) {
            console.warn('[RelatedPage] ⚠️ Could not fetch current post details:', fetchError);
          }
        }
      }
      
      if (!currentPostId) {
        setError('Unable to determine current article');
        return;
      }
      
      // Use optimized related posts API with language filtering
      const languageCategoryId = getCurrentLanguageCategoryId();
                  const relatedPosts = await fetchRelatedPosts(currentPostId, 30, languageCategoryId || undefined);
      
      if (relatedPosts && relatedPosts.length > 0) {
        console.log('[RelatedPage] ✅ Related posts loaded:', {
          count: relatedPosts.length,
          postId: currentPostId,
          hasCurrentPostContext: !!currentPostForTitle
        });
        
        setPosts(relatedPosts);
      } else {
        console.log('[RelatedPage] ⚠️ No related posts found');
        setError('No related articles found');
      }
      
    } catch (err) {
      console.error('[RelatedPage] ❌ Error loading related posts:', err);
      setError('Error occurred while loading related articles');
    } finally {
      setLoading(false);
    }
  }, [currentPost]);

  // Swipe-to-close handlers (simplified like TagPage)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isVisible) return;
    
    const touch = e.touches[0];
    setIsSwipeTracking(true);
    setSwipeStartX(touch.clientX);
    setSwipeStartY(touch.clientY);
    setCurrentSwipeX(touch.clientX);
    setSwipeProgress(0);
    
    console.log('[RelatedPage] 👆 Swipe tracking started:', { 
      startX: touch.clientX, 
      startY: touch.clientY 
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwipeTracking) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeStartX;
    const deltaY = Math.abs(touch.clientY - swipeStartY);
    
    // Only track right swipes (positive deltaX) and prevent if vertical scroll
    if (deltaX > 0 && deltaY < 50) {
      setCurrentSwipeX(touch.clientX);
      const progress = Math.min(deltaX / window.innerWidth, 1);
      setSwipeProgress(progress);
      
      // Enhanced visual feedback like TagPage
      if (containerRef.current) {
        const translateX = deltaX; // Use actual pixel distance
        const opacity = 1 - (progress * 0.4); // Less opacity change
        const scale = 1 - (progress * 0.03); // More subtle scale
        const blur = progress * 5; // Add blur effect
        
        containerRef.current.style.transform = `translateX(${translateX}px) scale(${scale}) translateZ(0)`;
        containerRef.current.style.opacity = `${opacity}`;
        containerRef.current.style.filter = `blur(${blur}px)`;
        containerRef.current.style.borderRadius = `${progress * 15}px 0 0 ${progress * 15}px`;
        containerRef.current.style.boxShadow = `-${progress * 20}px 0 ${30 + progress * 20}px rgba(0, 0, 0, ${0.3 + progress * 0.4})`;
      }
      
      console.log('[RelatedPage] 👆 Enhanced swipe in progress:', { 
        deltaX, 
        progress: progress.toFixed(2),
        translateX: deltaX
      });
    } else if (deltaY >= 50) {
      // Cancel swipe if user is scrolling vertically
      resetSwipeVisualFeedback();
      setIsSwipeTracking(false);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isSwipeTracking) return;
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - swipeStartX;
    const swipeDistance = Math.abs(deltaX);
    const swipeVelocity = swipeDistance / 200; // Simple velocity calculation
    
    setIsSwipeTracking(false);
    
    // Determine if swipe should close the page (same as TagPage)
    const shouldClose = (deltaX > 120) || (deltaX > 60 && swipeVelocity > 0.8);
    
    console.log('[RelatedPage] 👆 Swipe ended:', { 
      deltaX, 
      swipeDistance, 
      swipeVelocity: swipeVelocity.toFixed(2), 
      shouldClose,
      progress: swipeProgress.toFixed(2)
    });
    
    if (shouldClose) {
      // Close with enhanced animation (same as TagPage)
      console.log('[RelatedPage] ✅ Swipe-to-close triggered!');
      handleVisualClose();
    } else {
      // Snap back with smooth animation
      console.log('[RelatedPage] 🔙 Snapping back to position');
      resetSwipeVisualFeedback();
    }
  };

  const resetSwipeVisualFeedback = () => {
    if (containerRef.current) {
      containerRef.current.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.25s ease-out, border-radius 0.25s ease-out, filter 0.25s ease-out, box-shadow 0.25s ease-out';
      containerRef.current.style.transform = '';
      containerRef.current.style.opacity = '';
      containerRef.current.style.borderRadius = '';
      containerRef.current.style.filter = '';
      containerRef.current.style.boxShadow = '';
      
      // Clean up transition after animation
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.style.transition = '';
        }
      }, 250);
    }
    setSwipeProgress(0);
  };

  // Local visual close animation - like TagPage
  const handleVisualClose = () => {
    console.log('[RelatedPage] 🔙 Visual close animation triggered');
    if (containerRef.current) {
      containerRef.current.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.25s ease-out, filter 0.25s ease-out';
      containerRef.current.style.transform = 'translateX(100%) translateZ(0)';
      containerRef.current.style.opacity = '0';
      containerRef.current.style.filter = 'blur(5px)';
      
      setTimeout(() => {
        onClose();
        resetSwipeVisualFeedback();
      }, 250);
    } else {
      onClose();
    }
  };

  // Enhanced close function with animation (backward compatibility)
  const handleClose = () => {
    console.log('[RelatedPage] 🔙 Close button clicked');
    handleVisualClose();
  };

  // Load data when page becomes visible
  useEffect(() => {
    if (!isVisible) return;
    
    let isCancelled = false;
    
    // Add delay to show skeleton loading
    const timeoutId = setTimeout(() => {
      if (!isCancelled) {
        loadRelatedData();
      }
    }, 100);
    
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isVisible, loadRelatedData]);

  // Reset visual feedback when page becomes visible
  useEffect(() => {
    if (isVisible) {
      resetSwipeVisualFeedback();
    }
  }, [isVisible]);

  // History handling for mobile back button - DISABLED TO FIX NAVIGATION
  useEffect(() => {
    // TEMPORARILY DISABLED - This was interfering with post navigation
    // const handlePopState = (e: PopStateEvent) => {
    //   if (isVisible) {
    //     e.preventDefault();
    //     handleClose();
    //   }
    // };

    // if (isVisible) {
    //   window.addEventListener('popstate', handlePopState);
    //   window.history.pushState({ relatedPageOpen: true }, '');
    // }

    // return () => {
    //   window.removeEventListener('popstate', handlePopState);
    // };
  }, [isVisible]);

  // ENHANCED CLICK HANDLER - Inject related posts context for mobile TikTokFeed
  const handlePostClick = (post: WordPressPost, event?: any) => {
    const VERSION = "v3.0.0-related-context-injection";
    console.log(`[RelatedPage] ${VERSION} 🎯 ENHANCED CLICK HANDLER WITH RELATED CONTEXT:`, {
      id: post.id,
      slug: post.slug,
      title: post.title?.rendered?.substring(0, 50),
      isMobile,
      currentPostContext: currentPost ? { id: currentPost.id, title: (currentPost.title as any)?.rendered?.substring(0, 30) } : null,
      relatedPostsCount: posts.length,
      version: VERSION,
      timestamp: new Date().toISOString()
    });

    // Prevent default
    if (event) {
      console.log(`[RelatedPage] ${VERSION} 🛡️ Preventing default behaviors`);
      event.preventDefault();
      event.stopPropagation();
    }

    const url = `/post/${post.id}/${post.slug}.html`;
    console.log(`[RelatedPage] ${VERSION} 🎯 Target URL:`, url);
    
    if (isMobile && currentPost && posts.length > 0) {
      // 🎯 MOBILE: Inject related posts context để user có thể xem tất cả related posts
      console.log(`[RelatedPage] ${VERSION} 📱 Mobile navigation WITH RELATED CONTEXT injection`);
      console.log(`[RelatedPage] ${VERSION} 📱 Injecting related context:`, {
        sourcePostId: currentPost.id,
        sourcePostTitle: (currentPost.title as any)?.rendered?.substring(0, 50),
        targetPostId: post.id,
        relatedPostsCount: posts.length,
        contextType: 'related_posts'
      });
      
      // Close page first
      console.log(`[RelatedPage] ${VERSION} 🚪 Calling onClose()`);
      onClose();
      
      // Navigate với related context trong navigation state (delay để close animation hoàn thành)
      setTimeout(() => {
        console.log(`[RelatedPage] ${VERSION} 🚀 Navigating with related context injection...`);
        navigate(url, {
          state: {
            relatedContext: {
              sourcePostId: currentPost.id,
              sourcePostTitle: (currentPost.title as any)?.rendered,
              sourcePostTags: currentPost.tags || [],
              targetPostId: post.id,
              // Tiêm danh sách related posts để TikTokFeed có thể sử dụng
              relatedPosts: posts.slice(0, 25), // Gửi 25 posts đầu tiên để khởi tạo nhanh
              totalRelatedPosts: posts.length,
              contextType: 'related_posts'
            }
          }
        });
        console.log(`[RelatedPage] ${VERSION} ✅ Navigation completed with related context`);
      }, 150);
      
    } else {
      // 💻 PC hoặc không có context: Standard navigation
      console.log(`[RelatedPage] ${VERSION} 💻 Standard navigation (PC or no context)`);
      
      // Close page first
      console.log(`[RelatedPage] ${VERSION} 🚪 Calling onClose()`);
      onClose();
      
      // Use standard navigation method
      setTimeout(() => {
        console.log(`[RelatedPage] ${VERSION} 🚀 Using standard navigation...`);
        navigate(url);
        console.log(`[RelatedPage] ${VERSION} ✅ Standard navigation completed`);
      }, 100);
    }
  };

  return (
    <>
      {/* 🔍 SEO OPTIMIZED HELMET - Only render when RelatedPage is actually visible to prevent title conflicts */}
      {isVisible && (
        <Helmet>
          <title>{seoData.pageTitle}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <meta name="author" content="Infonium" />
          <meta property="og:title" content={seoData.ogTitle} />
          <meta property="og:description" content={seoData.description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/related'} />
          <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
          <meta property="og:site_name" content="Infonium" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@infonium" />
          <meta name="twitter:title" content={seoData.ogTitle} />
          <meta name="twitter:description" content={seoData.description} />
          <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
          <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/related'} />
          <meta name="robots" content="index, follow" />
          <meta name="language" content="vi" />
        </Helmet>
      )}

      <RelatedContainer 
        isVisible={isVisible} 
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Loading State - Show skeleton */}
        {loading && (
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <MobileGridSkeleton count={30} />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <ErrorContainer>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>😔</div>
            <h2>Oops!</h2>
            <p>{error}</p>
            <button 
              onClick={loadRelatedData}
              style={{
                marginTop: '16px',
                padding: '12px 24px',
                background: '#fff',
                color: '#000',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Thử lại
            </button>
          </ErrorContainer>
        )}

        {/* Success State - Use RelatedGrid component like TagPage */}
        {!loading && !error && posts.length > 0 && (
          <RelatedGrid
            tags={[]} // Empty tags array for now
            posts={posts}
            loading={false}
            loadingMore={false}
            hasMore={false}
            onLoadMore={() => {}} // Not needed for related posts
            onPostClick={handlePostClick}
            onClose={handleClose}
            currentPost={currentPost}
            shortTitle={seoData.shortTitle}
          />
        )}

        {/* Empty State */}
        {!loading && !error && posts.length === 0 && (
          <EmptyState>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <h3>No related articles yet</h3>
                          <p>Please check back later for more new content</p>
          </EmptyState>
        )}
      </RelatedContainer>
    </>
  );
};

export default RelatedPage;